/**
 * RequireAuth — gate that ensures the user is authenticated.
 *
 * Renders `children` once `me()` confirms the access token resolves to a real
 * user. On boot, with no in-memory token, we try `refresh()` once — the
 * refresh cookie may still be valid from a previous session. If that fails,
 * we redirect to `/login?next=<current-path>`.
 *
 * Subscribes to token changes so a logout in another part of the app forces a
 * re-evaluation here.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '@/lib/api';
import { getAccessToken, me, refresh, subscribeAccessToken, type MeOut } from '@/lib/auth';

type Status = 'loading' | 'authed' | 'redirecting';

type Props = {
  children: ReactNode | ((user: MeOut) => ReactNode);
  /**
   * Override how we redirect to login. Defaults to a `window.location.assign`
   * to `/login?next=<current>`. The router-aware version is supplied by the
   * routes layer once TanStack Router is wired in.
   */
  redirectToLogin?: (next: string) => void;
  /** Optional UI to show while we resolve the session. */
  fallback?: ReactNode;
};

function defaultRedirect(next: string): void {
  if (typeof window === 'undefined') return;
  const url = `/login?next=${encodeURIComponent(next)}`;
  window.location.assign(url);
}

function currentPath(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname + window.location.search;
}

export function RequireAuth({
  children,
  redirectToLogin = defaultRedirect,
  fallback = null,
}: Props): JSX.Element | null {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<MeOut | null>(null);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      // No token? Try refreshing from the httpOnly cookie before giving up.
      if (!getAccessToken()) {
        const refreshed = await refresh();
        if (cancelled) return;
        if (!refreshed) {
          setStatus('redirecting');
          redirectToLogin(currentPath());
          return;
        }
      }

      try {
        const profile = await me();
        if (cancelled) return;
        setUser(profile);
        setStatus('authed');
      } catch (err) {
        if (cancelled) return;
        // `apiRequest` already attempted one refresh-on-401; if `me()` still
        // throws 401 the session is truly gone.
        if (err instanceof ApiError && err.status === 401) {
          setStatus('redirecting');
          redirectToLogin(currentPath());
          return;
        }
        // For other errors we still redirect to login — surfacing them in this
        // gate would just deadlock the user.
        setStatus('redirecting');
        redirectToLogin(currentPath());
      }
    };

    void resolve();

    const unsub = subscribeAccessToken((token) => {
      if (cancelled) return;
      if (!token) {
        setStatus('redirecting');
        redirectToLogin(currentPath());
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [redirectToLogin]);

  if (status === 'authed' && user) {
    return <>{typeof children === 'function' ? children(user) : children}</>;
  }
  return <>{fallback}</>;
}

export default RequireAuth;
